<template>
    <div>
        <div class="container">
            <div class="form-row report-header">
                <div class="col-12 text-center">
                    <h5>Importar JSON</h5>
                </div>
            </div>

            <div class="form-row align-items-end mb-3 mt-4">
                <div class="col-md-3">
                    <label for="className">Nombre de la clase</label>
                    <input id="className" v-model.trim="className" type="text"
                        class="form-control" placeholder="Fee" @keydown.enter.prevent="process">
                </div>
                <div class="col-md-3">
                    <label>&nbsp;</label>
                    <div>
                        <button type="button" class="btn btn-primary" @click="openFile">
                            <font-awesome-icon icon="folder-open"/> Seleccionar archivo
                        </button>
                        <input id="import-json-file" type="file" accept=".json,application/json"
                            style="display: none;" @change="onFileChange" />
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="form-check mt-2">
                        <input id="overwrite" v-model="overwrite" type="checkbox" class="form-check-input">
                        <label class="form-check-label" for="overwrite">
                            Sobreescribir registros existentes
                        </label>
                    </div>
                </div>
            </div>

            <div class="form-row mb-2" v-if="fileName">
                <div class="col-12">
                    <small class="text-muted">Archivo: {{fileName}}</small>
                </div>
            </div>

            <div class="form-row mb-3">
                <div class="col-12">
                    <label for="jsonText">JSON</label>
                    <textarea id="jsonText" v-model="jsonText" class="form-control json-editor"
                        rows="18" spellcheck="false"></textarea>
                    <small class="text-muted">
                        Puede pegar o editar el JSON y procesar sin seleccionar un archivo.
                        Acepta un objeto o una lista de objetos. Las colecciones anidadas
                        (por ejemplo Amounts de Fee) se importan junto con el registro.
                    </small>
                </div>
            </div>

            <div class="form-row mb-3">
                <div class="col-12">
                    <button type="button" class="btn btn-success" @click="process"
                        :disabled="processing">
                        <font-awesome-icon icon="save"/> Procesar
                    </button>
                </div>
            </div>

            <div v-if="processing" class="text-center my-4">
                <img class="loading-gif" src="@/img/loading.gif">
            </div>

            <div v-if="result && !processing" class="alert" :class="resultAlertClass">
                <div>Se grabaron <strong>{{result.saved}}</strong> registros.</div>
                <div v-if="result.skipped">
                    {{result.skipped}} omitidos porque ya existían (marque sobreescribir para actualizarlos).
                </div>
                <div v-if="result.errors && result.errors.length">
                    {{result.errors.length}} con error:
                    <ul class="mb-0 mt-1">
                        <li v-for="(err, i) in result.errors" :key="i">
                            Registro {{err.index + 1}}: {{err.message}}
                        </li>
                    </ul>
                </div>
            </div>

            <div v-if="parseError" class="alert alert-danger">{{parseError}}</div>
        </div>
    </div>
</template>

<script>
import '@/css/app.css';

const serverurl = process.env.SERVER_ENDPOINT;

export default {
    name: 'import-json',
    data () {
        return {
            className: '',
            overwrite: false,
            jsonText: '',
            fileName: null,
            processing: false,
            result: null,
            parseError: null,
        }
    },
    computed: {
        resultAlertClass () {
            if (!this.result) return 'alert-info';
            if (this.result.errors && this.result.errors.length) return 'alert-warning';
            return 'alert-success';
        },
    },
    methods: {
        openFile () {
            let input = document.getElementById('import-json-file');
            input.value = null;
            input.click();
        },
        onFileChange (e) {
            let file = e.target.files[0];
            if (!file) return;
            this.fileName = file.name;
            this.result = null;
            this.parseError = null;
            let self = this;
            let reader = new FileReader();
            reader.onload = function (ev) {
                let text = ev.target.result || '';
                try {
                    let parsed = JSON.parse(text);
                    self.jsonText = JSON.stringify(parsed, null, 2);
                    self.parseError = null;
                } catch (err) {
                    self.jsonText = text;
                    self.parseError = 'El archivo no contiene un JSON válido. Puede editarlo antes de procesar.';
                }
            };
            reader.onerror = function () {
                self.parseError = 'Error al leer el archivo';
            };
            reader.readAsText(file, 'utf-8');
        },
        parseRecords () {
            let parsed;
            try {
                parsed = JSON.parse(this.jsonText);
            } catch (err) {
                throw new Error('El JSON no es válido. Revise el contenido del editor.');
            }
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.records) {
                parsed = parsed.records;
            }
            return parsed;
        },
        async process () {
            if (this.processing) return;
            this.parseError = null;
            this.result = null;
            if (!this.className) {
                this.parseError = 'Indique el nombre de la clase.';
                return;
            }
            if (!this.jsonText || !this.jsonText.trim()) {
                this.parseError = 'Pegue o edite el JSON, o seleccione un archivo.';
                return;
            }
            let records;
            try {
                records = this.parseRecords();
            } catch (err) {
                this.parseError = err.message;
                return;
            }
            this.processing = true;
            try {
                let resp = await fetch(serverurl + '/api/import_json/', {
                    method: 'POST',
                    mode: 'cors',
                    credentials: 'include',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        class_name: this.className,
                        overwrite: this.overwrite,
                        records: records,
                    }),
                });
                if (resp.status == 401) {
                    window.location.replace(window.location.origin + '/#/login');
                    return;
                }
                if (!resp.ok) {
                    let msg = await resp.text();
                    this.parseError = msg || ('Error ' + resp.status);
                    return;
                }
                this.result = await resp.json();
            } catch (err) {
                this.parseError = String(err);
            } finally {
                this.processing = false;
            }
        },
    },
}
</script>

<style scoped>
.json-editor {
    font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
    font-size: 13px;
    min-height: 320px;
    white-space: pre;
}
</style>
